import { BusinessOnboardingStepKey, IBusinessOnboardStep } from '@query'
import React from 'react'
import { FaCheckCircle, FaDotCircle, FaGift, FaMinusCircle } from 'react-icons/fa'
import styled, { css } from 'styled-components'
import { ActiveStepCard } from './ActiveStepCard'
import { CreditChip } from './CreditChip'
import { getStepCredits } from './companionCredits'
import { CompanionStepStatus, getChildProgress } from './companionStepState'
import type { GetStartedBankReturnKind } from './useBankHandover'

const ITEM_BORDER_WIDTH = '2px'

interface Props {
  step: IBusinessOnboardStep
  status: CompanionStepStatus
  isOpen: boolean
  isBursting: boolean
  onToggle: () => void
  onBankCta: () => void
  onGoToTransactions: () => void
  showBankSuccess: boolean
  bankReturnKind: GetStartedBankReturnKind | null
  onResolved: (groupKey: BusinessOnboardingStepKey, kind: 'done' | 'skipped') => void
  onCollapse: () => void
}

export const StepRow: React.FC<Props> = ({
  step,
  status,
  isOpen,
  isBursting,
  onToggle,
  onBankCta,
  onGoToTransactions,
  showBankSuccess,
  bankReturnKind,
  onResolved,
  onCollapse
}) => {
  const credits = getStepCredits(step)
  const childProgress = getChildProgress(step)
  const progress = step.progress && step.progress.total > 1 ? step.progress : childProgress
  const showProgress = status === 'todo' && progress.total > 1
  const showCoins =
    isBursting && credits > 0 && step.credits_granted_at !== null && status === 'done'

  return (
    <Item $open={isOpen}>
      {showCoins && (
        <CoinAnchor>
          <Coin $variant="a">
            <FaGift />
          </Coin>
          <Coin $variant="b">
            <FaGift />
          </Coin>
          <Coin $variant="c">
            <FaGift />
          </Coin>
          <Coin $variant="d" />
        </CoinAnchor>
      )}
      <Header type="button" aria-expanded={isOpen} onClick={onToggle} $open={isOpen}>
        <IconWrap $status={status} $bursting={isBursting}>
          <StatusIcon status={status} />
        </IconWrap>
        <Title $status={status} $open={isOpen}>
          {step.header}
        </Title>
        {showProgress && (
          <Progress>
            {progress.completed}/{progress.total}
          </Progress>
        )}
        <CreditChip
          amount={credits}
          tone={
            isOpen && status !== 'done' && status !== 'skipped'
              ? 'active'
              : step.credits_granted_at
              ? 'done'
              : status === 'skipped'
              ? 'skipped'
              : 'todo'
          }
        />
      </Header>
      {isOpen && (
        <ActiveStepCard
          step={step}
          onBankCta={onBankCta}
          onGoToTransactions={onGoToTransactions}
          showBankSuccess={showBankSuccess}
          bankReturnKind={bankReturnKind}
          onResolved={onResolved}
          onCollapse={onCollapse}
        />
      )}
    </Item>
  )
}

const StatusIcon: React.FC<{ status: CompanionStepStatus }> = ({ status }) => {
  if (status === 'done') return <FaCheckCircle />
  if (status === 'skipped') return <FaMinusCircle />
  return <FaDotCircle />
}

const Item = styled.div<{ $open: boolean }>`
  position: relative;
  border: ${ITEM_BORDER_WIDTH} solid
    ${({ $open, theme }) => ($open ? theme.colors.primary : 'transparent')};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
`

const Header = styled.button<{ $open: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem;
  border: none;
  border-radius: ${({ theme, $open }) => {
    const inner = `calc(${theme.borderRadius.md}rem - ${ITEM_BORDER_WIDTH})`
    return $open ? `${inner} ${inner} 0 0` : inner
  }};
  width: 100%;
  appearance: none;
  background: transparent;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.defaultHover};
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }
`

const IconWrap = styled.span<{ $status: CompanionStepStatus; $bursting: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  font-size: ${({ theme }) => theme.iconSize.xs}rem;
  line-height: 1;
  color: ${({ $status, theme }) =>
    $status === 'done'
      ? theme.colors.success
      : $status === 'in_progress'
      ? theme.colors.mutedYellow
      : theme.colors.borderStrong};

  ${({ $bursting }) =>
    $bursting &&
    css`
      animation: companionStepIconPop 0.42s cubic-bezier(0.2, 0.8, 0.3, 1);

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    `}

  @keyframes companionStepIconPop {
    0% {
      transform: scale(0.2);
    }
    55% {
      transform: scale(1.35);
    }
    100% {
      transform: scale(1);
    }
  }
`

const Title = styled.span<{ $status: CompanionStepStatus; $open: boolean }>`
  flex: 1;
  min-width: 0;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: ${({ $open }) => ($open ? 500 : 400)};
  color: ${({ $status, $open, theme }) =>
    $open
      ? theme.colors.neutralBlack
      : $status === 'done'
      ? `${theme.colors.neutralBlack}90`
      : $status === 'skipped'
      ? theme.colors.borderStrong
      : theme.colors.metalGray};
`

const Progress = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  font-family: 'Roboto Mono', ui-monospace, monospace;
  font-variant-numeric: tabular-nums;
`

const CoinAnchor = styled.span`
  position: absolute;
  left: ${({ theme }) => theme.spacing.md}rem;
  top: 0.7rem;
  width: 0;
  height: 0;
  pointer-events: none;

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`

const coinMotion = css<{ $variant: 'a' | 'b' | 'c' | 'd' }>`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  box-shadow: 0 1px 4px rgba(3, 30, 51, 0.3);
  animation-fill-mode: both;
  animation-timing-function: cubic-bezier(0.2, 0.7, 0.3, 1);

  svg {
    color: ${({ theme }) => theme.colors.neutralWhite};
  }

  ${({ $variant, theme }) =>
    $variant === 'a' &&
    `
      width: 1.0625rem;
      height: 1.0625rem;
      margin-left: -0.5rem;
      background: ${theme.colors.primary};
      animation: companionCoinA 900ms both;
      svg { font-size: 0.55rem; }
    `}

  ${({ $variant, theme }) =>
    $variant === 'b' &&
    `
      width: 0.875rem;
      height: 0.875rem;
      margin-left: -0.4375rem;
      background: ${theme.luca.sky};
      animation: companionCoinB 1000ms 80ms both;
      svg { font-size: 0.45rem; color: ${theme.colors.menuBg}; }
    `}

  ${({ $variant, theme }) =>
    $variant === 'c' &&
    `
      width: 0.9375rem;
      height: 0.9375rem;
      margin-left: -0.4375rem;
      background: ${theme.colors.primary};
      animation: companionCoinC 860ms 150ms both;
      svg { font-size: 0.5rem; }
    `}

  ${({ $variant, theme }) =>
    $variant === 'd' &&
    `
      width: 0.625rem;
      height: 0.625rem;
      margin-left: -0.3125rem;
      background: ${theme.dashboard.onNavyLabel};
      animation: companionCoinD 780ms 220ms both;
    `}

  @keyframes companionCoinA {
    0% {
      transform: translate(0, 4px) scale(0.3);
      opacity: 0;
    }
    25% {
      opacity: 1;
    }
    100% {
      transform: translate(-16px, -46px) scale(0.85);
      opacity: 0;
    }
  }
  @keyframes companionCoinB {
    0% {
      transform: translate(0, 4px) scale(0.3);
      opacity: 0;
    }
    25% {
      opacity: 1;
    }
    100% {
      transform: translate(6px, -58px) scale(0.7);
      opacity: 0;
    }
  }
  @keyframes companionCoinC {
    0% {
      transform: translate(0, 4px) scale(0.3);
      opacity: 0;
    }
    30% {
      opacity: 1;
    }
    100% {
      transform: translate(22px, -38px) scale(0.8);
      opacity: 0;
    }
  }
  @keyframes companionCoinD {
    0% {
      transform: translate(0, 4px) scale(0.3);
      opacity: 0;
    }
    30% {
      opacity: 1;
    }
    100% {
      transform: translate(-4px, -30px) scale(0.6);
      opacity: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const Coin = styled.span<{ $variant: 'a' | 'b' | 'c' | 'd' }>`
  ${coinMotion}
`
